import { Fragment, useState, type ReactElement } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { usePulseQuery } from "@stringf/hooks/queries/pulse";
import type { Pulse } from "@stringf/services/pulse";
import { DOMAIN_REQUEST_ID, DomainRequestForm } from "./DomainRequestForm";
import { fadeUp, stagger } from "./constants";
import { useSectionReveal } from "./useSectionReveal";
import { formatClock } from "@/lib/format-time";

/*
  The Pulse: who is already here, in the server's own numbers.

  This is the one place the page shows a figure, under the Claims Policy
  amendment of 2026-10-08 (PRODUCT.md). Everything below follows from that rule
  rather than from taste:

  - Every figure comes from `GET /api/v1/pulse` at view time and is shown with
    its as-of time. There is no number in a locale file; the strings carry a
    `{{formatted}}` hole and the component fills it. Never rounded, never "over".
  - A figure that is missing or zero is not shown, and a section with nothing to
    show is not rendered at all (no empty block, no "0 members").
  - Error and 503 (no snapshot yet) are the same outcome: the section is absent
    and the page still reads complete. `retry: false` in the hook, so a 503 hides
    the section at once instead of after a retry.
  - No count-up, ticker, pulsing dot, chart, or growth language. The numerals are
    set in the mono face with tabular figures so a column of them aligns, and
    that is the whole of the "data" treatment.
  - One institution (the expected launch state) is a sentence, not a one-row
    list: a list of one reads as sparse, a sentence reads as a statement.

  The live line is the same rule applied to `last24h`: each of its three figures
  is shown only when present and non-zero, the rest are left out with their
  separators, and with none left there is no line at all. It is a plain
  sentence in running text: no animation, no "live" mark, no comparison. The
  as-of stamp it needs is the one already beside the totals, not a second.

  The students/professionals split needs BOTH sides. The server sends `null` for
  a side under the Disclosure floor, `api.*` drops nulls, and showing one side
  alone would single out the other as the small one.

  Layout is two columns from `md`: the question and the totals on the left, the
  roll call on the right, so the list sits beside the question it answers. Below
  `md` it is one column in that same order.
*/

/** Numerals: the mono face, tabular. DESIGN.md §2 reserves mono for exactly this. */
const NUMERAL = "font-mono tabular-nums";

const LINK =
  "text-text-1 underline decoration-text-3 underline-offset-4 transition-colors hover:decoration-text-1";

const isFigure = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n) && n > 0;

/**
  One row per institution name. The server lists a name once per account type, so
  a university with students and staff above the Disclosure floor arrives twice;
  to a visitor it is one place. Order is first appearance, i.e. the server's.
*/
function mergeByName(
  rows: readonly { name: string; members: number }[]
): { name: string; members: number }[] {
  const merged = new Map<string, number>();
  for (const { name, members } of rows) {
    if (isFigure(members)) merged.set(name, (merged.get(name) ?? 0) + members);
  }
  return Array.from(merged, ([name, members]) => ({ name, members }));
}

/*
  Between two figures on one line. The no-break space glues the dot to the figure
  before it, so a wrap can only fall after the dot and never starts a line with
  one. Each figure is its own `whitespace-nowrap` item for the same reason: a
  wrap falls between a figure and its neighbour, never between a number and its
  unit.
*/
const SEPARATOR = "\u00a0· ";
const ITEM = "whitespace-nowrap";

/** HH:MM in the viewer's own zone, or null when `asOf` is not a time. */
function formatAsOf(asOf: string, lng: string): string | null {
  const at = new Date(asOf);
  if (Number.isNaN(at.getTime())) return null;
  /* `hour12: false` means h24 in some locales: "24:05" after midnight. */
  return formatClock(at, lng, { hourCycle: "h23" });
}

/** The reserved block shown while the request is in flight. Carries no copy. */
function PulseSkeleton() {
  /*
    Height reserved for the launch state in `mn`, the default locale (one
    sentence, the totals, the live line, the "not listed" line; measured at
    390px and 1280px with one institution) so the page below does not jump when the figures land.
    `aria-hidden`: there is nothing to read yet, and announcing a placeholder
    that may vanish would be noise.
  */
  const bar = "rounded-sm bg-surface motion-safe:animate-pulse";
  return (
    <section
      aria-hidden="true"
      className="relative z-10 px-6 pt-4 pb-16 md:pt-8 md:pb-24"
    >
      <div className="mx-auto grid min-h-[25.75rem] max-w-5xl content-start gap-y-10 md:min-h-[14.5rem] md:grid-cols-12 md:gap-x-12">
        <div className="space-y-4 md:col-span-5">
          <div className={`h-9 w-4/5 ${bar}`} />
          <div className={`h-9 w-3/5 ${bar}`} />
          <div className={`h-4 w-2/5 ${bar}`} />
        </div>
        <div className="space-y-4 md:col-span-7">
          <div className={`h-6 w-full ${bar}`} />
          <div className={`h-6 w-4/5 ${bar}`} />
          <div className={`h-4 w-3/5 ${bar}`} />
        </div>
      </div>
    </section>
  );
}

function PulseContent({ pulse, clock }: { pulse: Pulse; clock: string }) {
  const { t, i18n } = useTranslation();
  const [revealRef, revealed] = useSectionReveal<HTMLDivElement>();

  const nf = new Intl.NumberFormat(i18n.language);
  const institutions = mergeByName(pulse.institutions);
  const [requestOpen, setRequestOpen] = useState(false);
  const { total, students, professionals } = pulse.members;
  const showSplit = isFigure(students) && isFigure(professionals);

  const n: ReactElement = <span className={NUMERAL} />;
  const figure = (value: number) => ({ count: value, formatted: nf.format(value) });

  const { messages, stringsMade, activeRooms } = pulse.last24h;
  const live = (
    [
      ["pulse.live.messages", messages],
      ["pulse.live.strings", stringsMade],
      ["pulse.live.rooms", activeRooms],
    ] as const
  ).filter((entry): entry is (typeof entry & readonly [string, number]) => isFigure(entry[1]));

  return (
    <section
      id="pulse"
      aria-labelledby="pulse-title"
      className="relative z-10 scroll-mt-16 px-6 pt-4 pb-16 md:pt-8 md:pb-24"
    >
      <motion.div
        ref={revealRef}
        className="mx-auto grid max-w-5xl gap-y-10 md:grid-cols-12 md:gap-x-12"
        variants={stagger}
        initial="hidden"
        /* Degrades to *visible*, never to transparent. See useSectionReveal. */
        animate={revealed ? "visible" : "hidden"}
      >
        <div className="md:col-span-5">
          <motion.h2
            id="pulse-title"
            variants={fadeUp}
            className="font-display text-display font-light text-text-1 text-balance"
          >
            {t("pulse.title")}
          </motion.h2>

          <motion.div variants={fadeUp} className="mt-6 space-y-2">
            {isFigure(total) && (
              <p className="text-subhead text-text-1">
                <Trans t={t} i18nKey="pulse.total" components={{ n }} values={figure(total)} />
              </p>
            )}
            {live.length > 0 && (
              <p data-pulse-live className="text-ui text-text-1">
                {t("pulse.live.lead")}{" "}
                {live.map(([key, value], i) => (
                  <Fragment key={key}>
                    {i > 0 && <span aria-hidden="true">{SEPARATOR}</span>}
                    <span data-pulse-item className={ITEM}>
                      <Trans t={t} i18nKey={key} components={{ n }} values={figure(value)} />
                    </span>
                  </Fragment>
                ))}
              </p>
            )}
            {showSplit && (
              <p className="text-body text-text-2">
                <span className={ITEM}>
                  <Trans
                    t={t}
                    i18nKey="pulse.students"
                    components={{ n }}
                    values={figure(students)}
                  />
                </span>
                <span aria-hidden="true">{SEPARATOR}</span>
                <span className={ITEM}>
                  <Trans
                    t={t}
                    i18nKey="pulse.professionals"
                    components={{ n }}
                    values={figure(professionals)}
                  />
                </span>
              </p>
            )}
            <p className="text-meta text-text-3">
              <time dateTime={pulse.asOf}>
                <Trans t={t} i18nKey="pulse.asOf" components={{ n }} values={{ time: clock }} />
              </time>
            </p>
          </motion.div>
        </div>

        <div className="md:col-span-7">
          {institutions.length === 1 && (
            <motion.p
              variants={fadeUp}
              className="font-display text-subhead font-light text-text-1 md:text-title"
            >
              {/*
                The name is a component, not a `{{name}}` value: values are
                interpolated into the string before `Trans` parses it as markup
                (`escapeValue` is off app-wide), so a name like "<b>X</b>" would
                be read as a tag. As a child it is only ever text.
              */}
              <Trans
                t={t}
                i18nKey="pulse.single"
                components={{ n, name: <span>{institutions[0].name}</span> }}
                values={figure(institutions[0].members)}
              />
            </motion.p>
          )}

          {institutions.length > 1 && (
            <motion.div variants={fadeUp}>
              {/* The unit, once, for sighted readers; each row carries its own for a screen reader. */}
              <p aria-hidden="true" className="pb-2 text-right text-meta text-text-3">
                {t("pulse.membersCaption")}
              </p>
              <ul className="border-b border-surface-strong">
                {institutions.map((inst) => (
                  <li
                    key={inst.name}
                    className="flex items-baseline justify-between gap-6 border-t border-surface-strong py-3"
                  >
                    <span className="min-w-0 break-words text-ui text-text-1">{inst.name}</span>
                    <span className="shrink-0 text-ui text-text-2">
                      <span className={NUMERAL}>{nf.format(inst.members)}</span>
                      <span className="sr-only">
                        {" "}
                        {t("pulse.memberUnit", { count: inst.members })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          <motion.div
            variants={fadeUp}
            className={institutions.length > 0 ? "mt-8" : undefined}
          >
            <p className="text-body text-text-2">
              <Trans
                t={t}
                i18nKey="pulse.notListed"
                components={{
                  signup: <Link to="/signup" className={LINK} />,
                  request: (
                    <button
                      type="button"
                      aria-expanded={requestOpen}
                      aria-controls={requestOpen ? DOMAIN_REQUEST_ID : undefined}
                      onClick={() => setRequestOpen((open) => !open)}
                      className={LINK}
                    />
                  ),
                }}
              />
            </p>
            {requestOpen && (
              <div className="mt-5">
                <DomainRequestForm />
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

export function PulseSection() {
  const { i18n } = useTranslation();
  const { data, isPending } = usePulseQuery();

  if (isPending) return <PulseSkeleton />;
  /* Error or 503: absent. The page reads complete without it. */
  if (!data) return null;

  /* A figure without its as-of time may not be shown (Claims Policy). */
  const clock = formatAsOf(data.asOf, i18n.language);
  const anything =
    isFigure(data.members.total) || data.institutions.some((i) => isFigure(i.members));
  if (!clock || !anything) return null;

  return <PulseContent pulse={data} clock={clock} />;
}
